import { ReactElement, useCallback, useEffect, useState } from 'react';

import { JiraAuthDetails, JiraCloudTicketCreator } from '@evinced-private/ui-common';

import { PlatformPopup } from 'src/common/components/platform-popup/PlatformPopup';
import { asyncHandler } from 'src/common/helpers/AsyncHelper';
import { JiraApi } from 'src/common/services/api/JiraApi';
import { getJiraAccessToken } from 'src/common/services/api/LocalStorageApi';
import { screenshotService } from 'src/common/services/ScreenshotService';
import { JiraCloudData } from 'src/common/types/JiraTypes';
import { Report } from 'src/common/types/ReportModel';

import { handleJiraTicketCreated } from './helpers/handleJiraTicketCreated';
import { JiraTicketCreatedCallback } from './TicketsSystems.types';

import './JiraCloudModule.scss';

const clientID = import.meta.env.VITE_JIRA_APP_CLIENT_ID;
const clientSecret = import.meta.env.VITE_JIRA_APP_CLIENT_SECRET;

function getJiraAuthDetails(): JiraAuthDetails {
	const jiraToken = JSON.parse(getJiraAccessToken() || '{}');
	return {
		token: jiraToken?.access_token,
		clientId: clientID,
		clientSecret,
		refreshToken: jiraToken?.refresh_token
	};
}

export const JiraCloudModule = ({
	issueToExport,
	setJiraModalOpened,
	isJiraModalOpened,
	onTicketCreated
}: {
	issueToExport: Report;
	setJiraModalOpened: (isOpen: boolean) => void;
	isJiraModalOpened: boolean;
	onTicketCreated?: JiraTicketCreatedCallback;
}): ReactElement => {
	const [jiraHost, setJiraHost] = useState<string>('');
	const [resolvedScreenshot, setResolvedScreenshot] = useState<string>(null);

	const resolveScreenshotWithBoundingBoxes = useCallback(async (): Promise<void> => {
		if (issueToExport?.screenshotDetails) {
			const [screenshotImageData] = await asyncHandler(
				screenshotService.getFullPageScreenshotWithIssues(issueToExport.screenshotDetails)
			);
			if (screenshotImageData && typeof screenshotImageData === 'string') {
				setResolvedScreenshot(screenshotImageData);
			}
		} else if (issueToExport?.pageScreenshot) {
			// Fallback to the original screenshot if no screenshotDetails available
			setResolvedScreenshot(issueToExport.pageScreenshot);
		}
	}, [issueToExport]);

	useEffect(() => {
		const getJiraHost = async (): Promise<void> => {
			const [data] = await asyncHandler<JiraCloudData[]>(
				JiraApi.getJiraCloudId(),
				'Cannot get getJiraCloudId'
			);
			if (data?.[0]) {
				setJiraHost(data?.[0].url);
			}
		};
		getJiraHost();
	}, []);

	useEffect(() => {
		resolveScreenshotWithBoundingBoxes();
	}, [resolveScreenshotWithBoundingBoxes]);

	const jiraAuthDetails = getJiraAuthDetails();
	const initialValues = {
		summary: issueToExport?.summary,
		description: issueToExport?.description,
		labels: issueToExport?.labels ?? [],
		screenshots: resolvedScreenshot ? [resolvedScreenshot] : []
	};
	return (
		<PlatformPopup
			popupWidth={1050}
			className="jira-cloud-module"
			isControlled
			onClose={() => setJiraModalOpened(false)}
			title="Create Jira Issue"
			isOpen={isJiraModalOpened}
		>
			<JiraCloudTicketCreator
				authDetails={jiraAuthDetails}
				initialValues={initialValues}
				onAbort={(): void => {
					setJiraModalOpened(false);
				}}
				onFailure={(): void => {
					setJiraModalOpened(false);
				}}
				onSuccess={(ticketId): void => {
					if (!ticketId) {
						return;
					}
					handleJiraTicketCreated({ ticketId, jiraHost, onTicketCreated });
					setJiraModalOpened(false);
				}}
			/>
		</PlatformPopup>
	);
};
